import { useEffect, useRef, useState } from "react"
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query"
import {
  nutritionQueueQuerySchema,
  type NutritionQueue,
  type PimWorkflowDetail,
  nutritionQueuePeriodSchema,
  nutritionQueueSchema,
  pimWorkflowDetailSchema,
  routes,
} from "@nova-ai/shared"
import { z } from "zod"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export const nutritionQueueSearchSchema = z.object({
  period: nutritionQueuePeriodSchema.optional(),
  q: z.string().trim().max(128).optional(),
  view: nutritionQueueQuerySchema.shape.view,
  step: nutritionQueueQuerySchema.shape.step,
  event: z.coerce.number().int().positive().optional(),
  store: z.string().max(64).optional(),
  sku: z.string().max(128).optional(),
  scope: z.string().min(1).max(300).optional(),
  scopes: z.literal("failed").optional(),
})
export type NutritionQueueSearch = z.infer<typeof nutritionQueueSearchSchema>

// Complete receipt aggregation is more expensive than a single history page.
export function nutritionQueuePollInterval(paused: boolean) {
  return paused ? false : 60_000
}

export function useNutritionQueue(search: NutritionQueueSearch) {
  const [paused, setPaused] = useState(false)
  const queryClient = useQueryClient()
  const period = search.period ?? "7d"
  const previousPeriod = useRef(period)
  const filter = nutritionQueueQuerySchema.parse({
    period: search.period,
    search: search.q,
    view: search.view,
    step: search.step,
    limit: 20,
  })
  const common = {
    refetchInterval: nutritionQueuePollInterval(paused),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: 1,
  } as const
  const list = useInfiniteQuery({
    queryKey: queryKeys.nutritionQueue(filter),
    placeholderData: (
      previous: InfiniteData<NutritionQueue, string | undefined> | undefined,
      query
    ) => (query?.queryKey[1].period === filter.period ? previous : undefined),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<unknown>(routes.pimEvents.nutritionQueue(), {
        signal,
        query: { ...filter, before: pageParam },
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return nutritionQueueSchema.parse(result.value)
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    ...common,
  })
  // Run after the observer changes periods. Its previous query can still be active.
  useEffect(() => {
    const previous = previousPeriod.current
    previousPeriod.current = period
    if (previous === period) {
      return
    }
    queryClient.removeQueries({
      queryKey: ["nutrition-queue"],
      predicate: (query) =>
        nutritionQueueQuerySchema.safeParse(query.queryKey[1]).data?.period === previous,
    })
  }, [period, queryClient])
  const detail = useInfiniteQuery({
    queryKey: queryKeys.nutritionQueueDetail(search.event, search.scopes),
    placeholderData: (
      previous: InfiniteData<PimWorkflowDetail, string | undefined> | undefined,
      query
    ) => (query?.queryKey[1] === search.event ? previous : undefined),
    initialPageParam: undefined as string | undefined,
    enabled: search.event !== undefined,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<unknown>(
        routes.pimEvents.historyDetail({ receiptId: String(search.event) }),
        {
          signal,
          query: {
            targetAfter: pageParam,
            targetStatus: search.scopes,
            targetLimit: 25,
            attemptLimit: 25,
          },
        }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return pimWorkflowDetailSchema.parse(result.value)
    },
    getNextPageParam: (page) => page.nextTargetCursor ?? undefined,
    ...common,
  })
  const attempts = useInfiniteQuery({
    queryKey: queryKeys.nutritionQueueAttempts(search.event),
    enabled: search.event !== undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<unknown>(
        routes.pimEvents.historyDetail({ receiptId: String(search.event) }),
        { signal, query: { attemptAfter: pageParam, targetLimit: 1, attemptLimit: 25 } }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return pimWorkflowDetailSchema.parse(result.value)
    },
    getNextPageParam: (page) => page.nextAttemptCursor ?? undefined,
    ...common,
  })
  const selection = useQuery({
    queryKey: queryKeys.nutritionQueueSelection(
      search.event,
      search.store,
      search.sku,
      search.scope
    ),
    enabled:
      search.event !== undefined &&
      (search.scope !== undefined || (search.store !== undefined && search.sku !== undefined)),
    queryFn: async ({ signal }) => {
      const result = await apiFetch<unknown>(
        routes.pimEvents.historyDetail({ receiptId: String(search.event) }),
        {
          signal,
          query: {
            store: search.store,
            sku: search.sku,
            scopeId: search.scope,
            targetLimit: search.scope ? 1 : 100,
            attemptLimit: 100,
          },
        }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return pimWorkflowDetailSchema.parse(result.value)
    },
    ...common,
  })
  return { list, detail, paused, setPaused, attempts, selection }
}
